import { BybButton } from "@Components/common/byb-ds";
import BybMedalElement from "@Components/common/byb-ds/byb-medal-element";
import { GameModeCard, GameModeOptionButton } from "@Components/game/game-mode";
import { usePractiseContext } from "@Context/game/practise/practise-context";
import { AnalyticsEvents } from "@Enums/index";
import Leagues from "@Enums/Leagues";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import { useSelectionDisplayText } from "@hooks/game/selection/use-selection-display-text";
import { usePractise } from "@hooks/game/use-practise";
import useI18n from "@i18n";
import { ChevronRight } from "@mui/icons-material";
import { Button, Stack, Typography, useTheme } from "@mui/material";

const PractiseOverview = () => {
  const theme = useTheme();
  const { i18n } = useI18n();
  const { getPhaseSelectionDisplayText, getPositionSelectionDisplayText } = useSelectionDisplayText();
  const {
    selectedPositions,
    openPositionDialog,
    selectedPhases,
    openPhasesDialog,
    onPractiseSessionStart,
    isAboveMax,
    selectedLeague,
    selectedDivision,
  } = usePractiseContext();

  const { track } = useAnalytics();

  const practiseData = usePractise();

  const handleStartSession = () => {
    if (isAboveMax) {
      return;
    }

    onPractiseSessionStart();
    track(AnalyticsEvents.practice_started, { ...practiseData });
  };

  const leagueTitle = selectedLeague ? i18n(`league.${selectedLeague.name}.name`) : i18n("practice.mode");

  return (
    <GameModeCard flex={0.6} sx={{ padding: theme.spacing(8) }}>
      <Stack gap={3}>
        {selectedLeague && <BybMedalElement league={selectedLeague.name as Leagues} size={12} sx={{ alignSelf: "center" }} />}
        <Stack spacing={2} flex={0.5} justifyContent={"center"}>
          <Typography variant="text6xl800" color="dsBase.v200">
            {leagueTitle}
          </Typography>
          <Typography variant="text1xl500" color="dsNeutral.v300">
            {i18n("practice.description")}
          </Typography>
          {selectedDivision && (
            <Stack
              alignItems="center"
              justifyContent="center"
              bgcolor={`${theme.palette.dsSurface.v300}80`}
              borderRadius={theme.spacing(10)}
              border={`${theme.spacing(0.25)} solid ${theme.palette.dsSurface.v400}80`}
              padding={theme.spacing(1.5, 2.5)}
              sx={{ alignSelf: "center" }}
            >
              <Typography variant="textLg600" color="dsBase.v200">
                {i18n("division.specific", { division: selectedDivision.name })}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
      <Stack spacing={3} flex={0.25}>
        <Stack direction="row" spacing={2}>
          <GameModeOptionButton onClick={openPositionDialog}>
            <Typography variant="text1xl600" color="dsBase.v200">
              {getPositionSelectionDisplayText(selectedPositions)}
            </Typography>
            <ChevronRight sx={{ color: theme.palette.dsNeutral.v200 }} fontSize="large" />
          </GameModeOptionButton>
          <GameModeOptionButton onClick={openPhasesDialog}>
            <Typography variant="text1xl600" color="dsBase.v200">
              {getPhaseSelectionDisplayText(selectedPhases)}
            </Typography>
            <ChevronRight sx={{ color: theme.palette.dsNeutral.v200 }} fontSize="large" />
          </GameModeOptionButton>
        </Stack>
        <Button size="large" onClick={handleStartSession} sx={{ flex: 1 }} disabled={isAboveMax}>
          {i18n("play")}
        </Button>
      </Stack>
    </GameModeCard>
  );
};

export default PractiseOverview;
